import type { CanadianNote } from '../../../../methods/src/tables/memos';

/**
 * The reusable "Canadian content note" — additive, Canada-only orientation that
 * appears in a memo only when the member's Canada Market Lens is on. Shared
 * across the Business Case funding note (SR&ED/IRAP/BDC), the IP filing note
 * (CIPO), and the clinical Health Canada note, so all three read as one
 * deliberate second lens rather than core memo content promoted to peer status.
 *
 * Two mounting variants:
 *  - `panel`  — a contained, tinted aside on the parchment page (Business Case,
 *               IP memo). A full all-sides hairline, never a single-side accent.
 *  - `nested` — no own surface; separates from the content above with a top
 *               rule that outranks the internal item dividers (clinical memos,
 *               inside the already-gray regulatory panel).
 *
 * Canadian-ness is carried by the word (a `CANADA LENS` kicker + the topic
 * title), never a flag or country color — the same achromatic discipline the
 * derived market figures hold. `whatItIs` is factual (full ink); `mightFit` is
 * the hedged, idea-specific read (italic, muted, tagged). The closing hand-off
 * reads as a quiet caveat.
 */
function MapPinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
      <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />
    </svg>
  );
}

function UserCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
      <path d="M6 21v-2a4 4 0 0 1 4 -4h4" />
      <path d="M15 19l2 2l4 -4" />
    </svg>
  );
}

export function CanadianNoteBlock({
  data,
  variant = 'panel',
}: {
  data: CanadianNote;
  variant?: 'panel' | 'nested';
}) {
  return (
    <div className={`ccn ${variant === 'nested' ? 'ccn--nested' : 'ccn--panel'}`}>
      <div className="ccn-kick">
        <MapPinIcon /> Canada lens
      </div>
      <div className="ccn-title">{data.title}</div>
      {data.intro ? <p className="ccn-intro">{data.intro}</p> : null}
      {data.programs.map((p) => (
        <div className="ccn-prog" key={p.name}>
          <div className="ccn-name">{p.name}</div>
          <p className="ccn-what">{p.whatItIs}</p>
          {p.mightFit ? (
            <p className="ccn-fit">
              <span className="tag">Might fit</span>
              {p.mightFit}
            </p>
          ) : null}
        </div>
      ))}
      <div className="ccn-off">
        <UserCheckIcon /> Orientation only · confirm with {data.handoff}
      </div>
    </div>
  );
}
